(function () {
  'use strict';

  var SELECTOR_ROOT = '[data-epic-scroll-socks]';
  var SELECTOR_SOCK = '[data-epic-scroll-sock]';
  var MOBILE_QUERY = '(max-width: 749px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  function easeInOutCubic(progress) {
    if (progress < 0.5) {
      return 4 * progress * progress * progress;
    }

    return 1 - Math.pow(-2 * progress + 2, 3) / 2;
  }

  function quadraticBezier(start, control, end, progress) {
    var inverse = 1 - progress;

    return (
      inverse * inverse * start +
      2 * inverse * progress * control +
      progress * progress * end
    );
  }

  function getCssNumber(element, propertyName, fallback) {
    var value = parseFloat(
      window.getComputedStyle(element).getPropertyValue(propertyName)
    );

    return Number.isFinite(value) ? value : fallback;
  }

  function getDataNumber(element, attributeName, fallback) {
    var value = parseFloat(element.getAttribute(attributeName));

    return Number.isFinite(value) ? value : fallback;
  }

  function isMobile() {
    return window.matchMedia &&
      window.matchMedia(MOBILE_QUERY).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  function ScrollSocksBanner(root) {
    this.root = root;
    this.media = root.querySelector(
      '.epic-scroll-socks-banner__media'
    );
    this.socks = Array.prototype.slice.call(
      root.querySelectorAll(SELECTOR_SOCK)
    );

    this.ticking = false;
    this.destroyed = false;

    this.handleRequestUpdate = this.requestUpdate.bind(this);
    this.handleSectionUnload = this.handleSectionUnload.bind(this);

    if (!this.media || !this.socks.length) {
      return;
    }

    this.bindEvents();
    this.update();
    this.root.classList.add('is-ready');
  }

  ScrollSocksBanner.prototype.bindEvents = function () {
    window.addEventListener(
      'scroll',
      this.handleRequestUpdate,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      this.handleRequestUpdate
    );

    window.addEventListener(
      'load',
      this.handleRequestUpdate
    );

    document.addEventListener(
      'shopify:section:unload',
      this.handleSectionUnload
    );
  };

  ScrollSocksBanner.prototype.unbindEvents = function () {
    window.removeEventListener(
      'scroll',
      this.handleRequestUpdate
    );

    window.removeEventListener(
      'resize',
      this.handleRequestUpdate
    );

    window.removeEventListener(
      'load',
      this.handleRequestUpdate
    );

    document.removeEventListener(
      'shopify:section:unload',
      this.handleSectionUnload
    );
  };

  ScrollSocksBanner.prototype.handleSectionUnload = function (event) {
    if (!event || !event.target) return;

    if (event.target.contains(this.root)) {
      this.destroy();
    }
  };

  ScrollSocksBanner.prototype.destroy = function () {
    if (this.destroyed) return;

    this.destroyed = true;
    this.unbindEvents();
  };

  ScrollSocksBanner.prototype.requestUpdate = function () {
    var self = this;

    if (this.destroyed || this.ticking) return;

    this.ticking = true;

    window.requestAnimationFrame(function () {
      self.update();
      self.ticking = false;
    });
  };

  ScrollSocksBanner.prototype.getProgress = function () {
    if (prefersReducedMotion()) {
      return 1;
    }

    var rect = this.root.getBoundingClientRect();
    var viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      1;

    var startPercent = getDataNumber(
      this.root,
      'data-scroll-start',
      88
    );

    var endPercent = getDataNumber(
      this.root,
      'data-scroll-end',
      25
    );

    var startPosition = viewportHeight * (startPercent / 100);
    var endPosition = viewportHeight * (endPercent / 100);

    if (startPosition === endPosition) {
      return rect.top <= endPosition ? 1 : 0;
    }

    var rawProgress =
      (startPosition - rect.top) /
      (startPosition - endPosition);

    return clamp(rawProgress, 0, 1);
  };

  ScrollSocksBanner.prototype.updateSock = function (
    sock,
    progress,
    mediaWidth,
    mediaHeight,
    mobile
  ) {
    var startXPercent = getCssNumber(
      this.root,
      mobile
        ? '--epic-start-x-mobile'
        : '--epic-start-x-desktop',
      20
    );

    var startYPercent = getCssNumber(
      this.root,
      mobile
        ? '--epic-start-y-mobile'
        : '--epic-start-y-desktop',
      43
    );

    var endXPercent = getCssNumber(
      sock,
      mobile
        ? '--epic-end-x-mobile'
        : '--epic-end-x-desktop',
      50
    );

    var endYPercent = getCssNumber(
      sock,
      mobile
        ? '--epic-end-y-mobile'
        : '--epic-end-y-desktop',
      50
    );

    var curveXPercent = getDataNumber(
      sock,
      mobile
        ? 'data-curve-x-mobile'
        : 'data-curve-x-desktop',
      0
    );

    var curveYPercent = getDataNumber(
      sock,
      mobile
        ? 'data-curve-y-mobile'
        : 'data-curve-y-desktop',
      0
    );

    var sockWidth = sock.offsetWidth || 0;
    var sockHeight = sock.offsetHeight || sockWidth;

    var startX =
      mediaWidth * (startXPercent / 100) -
      sockWidth / 2;

    var startY =
      mediaHeight * (startYPercent / 100) -
      sockHeight / 2;

    var endX =
      mediaWidth * (endXPercent / 100) -
      sockWidth / 2;

    var endY =
      mediaHeight * (endYPercent / 100) -
      sockHeight / 2;

    var controlX =
      (startX + endX) / 2 +
      mediaWidth * (curveXPercent / 100);

    var controlY =
      (startY + endY) / 2 +
      mediaHeight * (curveYPercent / 100);

    var easedProgress = easeInOutCubic(progress);

    var currentX = quadraticBezier(
      startX,
      controlX,
      endX,
      easedProgress
    );

    var currentY = quadraticBezier(
      startY,
      controlY,
      endY,
      easedProgress
    );

    var startScale = getCssNumber(
      sock,
      '--epic-start-scale',
      0.4
    );

    var currentScale = lerp(
      startScale,
      1,
      easedProgress
    );

    var startRotation = getCssNumber(
      sock,
      '--epic-start-rotation',
      0
    );

    var middleRotation = getCssNumber(
      sock,
      '--epic-middle-rotation',
      0
    );

    var endRotation = getCssNumber(
      sock,
      '--epic-end-rotation',
      0
    );

    var currentRotation;

    if (easedProgress <= 0.5) {
      currentRotation = lerp(
        startRotation,
        middleRotation,
        easedProgress * 2
      );
    } else {
      currentRotation = lerp(
        middleRotation,
        endRotation,
        (easedProgress - 0.5) * 2
      );
    }

    sock.style.setProperty(
      '--epic-current-x',
      currentX.toFixed(2) + 'px'
    );

    sock.style.setProperty(
      '--epic-current-y',
      currentY.toFixed(2) + 'px'
    );

    sock.style.setProperty(
      '--epic-current-scale',
      currentScale.toFixed(4)
    );

    sock.style.setProperty(
      '--epic-current-rotation',
      currentRotation.toFixed(2) + 'deg'
    );
  };

  ScrollSocksBanner.prototype.update = function () {
    if (
      this.destroyed ||
      !this.media ||
      !this.socks.length
    ) {
      return;
    }

    var mediaRect = this.media.getBoundingClientRect();

    if (mediaRect.width <= 0 || mediaRect.height <= 0) {
      return;
    }

    var viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight;

    var isFarOutsideViewport =
      mediaRect.bottom < -viewportHeight ||
      mediaRect.top > viewportHeight * 2;

    if (isFarOutsideViewport) {
      return;
    }

    var progress = this.getProgress();
    var mobile = isMobile();

    if (progress >= 0.995) {
  this.root.classList.add('is-complete');
} else {
  this.root.classList.remove('is-complete');
}

    for (var index = 0; index < this.socks.length; index += 1) {
      this.updateSock(
        this.socks[index],
        progress,
        mediaRect.width,
        mediaRect.height,
        mobile
      );
    }

    this.root.classList.add('is-ready');
  };

  function initializeSection(root) {
    if (!root || root.dataset.epicScrollSocksInitialized === 'true') {
      return;
    }

    root.dataset.epicScrollSocksInitialized = 'true';
    root.epicScrollSocksInstance = new ScrollSocksBanner(root);
  }

  function initializeAll(container) {
    var scope = container || document;
    var roots = scope.querySelectorAll(SELECTOR_ROOT);

    for (var index = 0; index < roots.length; index += 1) {
      initializeSection(roots[index]);
    }
  }

  function handleSectionLoad(event) {
    if (!event || !event.target) return;

    if (event.target.matches && event.target.matches(SELECTOR_ROOT)) {
      initializeSection(event.target);
      return;
    }

    initializeAll(event.target);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    });
  } else {
    initializeAll(document);
  }

  document.addEventListener(
    'shopify:section:load',
    handleSectionLoad
  );
})();